import { __export } from './chunk-PZ5AY32C.js';
import { Schema, Option, Effect } from 'effect';
import { Stateful, Icon, Spinner, Button, Badge, Alert, Textarea } from '@foldworks/ui';
import { defineMessageUnion } from 'foldkit/message';
import { Sparkles, Check, Copy, RefreshCcw, LockKeyhole, CheckCircle2, CircleX, Wrench, User, Bot, RotateCcw, CircleStop, Send } from '@lucide/icons';
import * as stylex from '@stylexjs/stylex';
import { Command, Update } from 'foldkit';

// src/agent.ts
var agent_exports = {};
__export(agent_exports, {
  Chat: () => Chat,
  Composer: () => Composer,
  ConversationPart: () => ConversationPart2,
  ConversationTurn: () => ConversationTurn,
  EmptyState: () => EmptyState,
  EventEnvelope: () => EventEnvelope2,
  Message: () => Message2,
  MessageActions: () => MessageActions,
  Model: () => Model2,
  ModelSelect: () => ModelSelect,
  PermissionRequest: () => PermissionRequest,
  ReasoningPanel: () => ReasoningPanel,
  ReasoningPart: () => ReasoningPart2,
  RunState: () => RunState2,
  Segment: () => Segment2,
  SessionBar: () => SessionBar,
  StreamEvent: () => StreamEvent2,
  TextPart: () => TextPart2,
  TextResponse: () => TextResponse,
  ToolCall: () => ToolCall,
  ToolPart: () => ToolPart2,
  Transcript: () => Transcript,
  Turn: () => Turn2,
  init: () => init,
  isActive: () => isActive,
  latestUserPrompt: () => latestUserPrompt,
  statusLabel: () => statusLabel,
  transcriptId: () => transcriptId,
  turnElementId: () => turnElementId,
  turnText: () => turnText,
  update: () => update
});
var Segment = Schema.Literals(["Initial", "Approved", "Denied"]);
var StreamEvent = Schema.Union([Schema.Struct({
  _tag: Schema.Literal("Started"),
  runId: Schema.String,
  modelId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("TextStarted"),
  runId: Schema.String,
  partId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("TextDelta"),
  runId: Schema.String,
  partId: Schema.String,
  delta: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("TextFinished"),
  runId: Schema.String,
  partId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ReasoningStarted"),
  runId: Schema.String,
  partId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ReasoningDelta"),
  runId: Schema.String,
  partId: Schema.String,
  delta: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ReasoningFinished"),
  runId: Schema.String,
  partId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ToolInputStarted"),
  runId: Schema.String,
  callId: Schema.String,
  name: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ToolInputDelta"),
  runId: Schema.String,
  callId: Schema.String,
  delta: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ToolCallReady"),
  runId: Schema.String,
  callId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ToolStarted"),
  runId: Schema.String,
  callId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("ToolResult"),
  runId: Schema.String,
  callId: Schema.String,
  output: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("PermissionRequested"),
  runId: Schema.String,
  callId: Schema.String,
  reason: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("Finished"),
  runId: Schema.String
}), Schema.Struct({
  _tag: Schema.Literal("Failed"),
  runId: Schema.String,
  message: Schema.String
})]);
var EventEnvelope = Schema.Struct({
  sequence: Schema.Int,
  event: StreamEvent
});

// src/message.ts
var Message = defineMessageUnion({
  GotModelPickerMessage: {
    message: Stateful.Select.Message
  },
  ChangedDraft: {
    value: Schema.String
  },
  Submitted: {},
  SelectedSuggestion: {
    prompt: Schema.String
  },
  ReceivedStreamEvent: {
    envelope: EventEnvelope
  },
  ChosePermission: {
    decision: Schema.Literals(["Allow", "Deny"])
  },
  Stopped: {},
  Retried: {},
  RegeneratedTurn: {
    turnId: Schema.String
  },
  CopiedTurn: {
    turnId: Schema.String
  },
  CompletedCopyTurn: {
    turnId: Schema.String
  },
  ClearedCopiedTurn: {
    turnId: Schema.String
  },
  Reset: {},
  ScrolledTranscript: {
    scrollTop: Schema.Number
  },
  CompletedMeasureTranscript: {
    isFollowing: Schema.Boolean,
    currentTurnId: Schema.String,
    visibleTurnIds: Schema.Array(Schema.String)
  },
  JumpedLatest: {},
  JumpedToTurn: {
    turnId: Schema.String
  },
  CompletedScrollLatest: {},
  CompletedScrollToTurn: {
    turnId: Schema.String
  }
});
var TextPart = Schema.Struct({
  _tag: Schema.Literal("Text"),
  id: Schema.String,
  text: Schema.String,
  status: Schema.Literals(["Streaming", "Complete", "Interrupted", "Failed"])
});
var ReasoningPart = Schema.Struct({
  _tag: Schema.Literal("Reasoning"),
  id: Schema.String,
  text: Schema.String,
  status: Schema.Literals(["Streaming", "Complete", "Interrupted", "Failed"])
});
var ToolPart = Schema.Struct({
  _tag: Schema.Literal("Tool"),
  callId: Schema.String,
  name: Schema.String,
  input: Schema.String,
  output: Schema.String,
  permissionReason: Schema.String,
  status: Schema.Literals(["Preparing", "Running", "WaitingApproval", "Completed", "Denied", "Cancelled", "Failed"])
});
var ConversationPart = Schema.Union([TextPart, ReasoningPart, ToolPart]);
var Turn = Schema.Struct({
  id: Schema.String,
  role: Schema.Literals(["User", "Assistant"]),
  modelId: Schema.String,
  parts: Schema.Array(ConversationPart)
});
var RunState = Schema.Union([Schema.Struct({
  _tag: Schema.Literal("Idle")
}), Schema.Struct({
  _tag: Schema.Literal("Streaming"),
  runId: Schema.String,
  segment: Segment,
  lastSequence: Schema.Int
}), Schema.Struct({
  _tag: Schema.Literal("AwaitingPermission"),
  runId: Schema.String,
  callId: Schema.String,
  lastSequence: Schema.Int
}), Schema.Struct({
  _tag: Schema.Literal("Failed"),
  runId: Schema.String,
  message: Schema.String
})]);
var Model = Schema.Struct({
  id: Schema.String,
  transcript: Schema.Array(Turn),
  draft: Schema.String,
  selectedModel: Schema.String,
  defaultModel: Schema.String,
  modelPicker: Stateful.Select.Model,
  runState: RunState,
  nextRunNumber: Schema.Int,
  isFollowing: Schema.Boolean,
  anchoredTurnId: Schema.String,
  currentTurnId: Schema.String,
  visibleTurnIds: Schema.Array(Schema.String),
  copiedTurnId: Schema.String,
  announcement: Schema.String
});
var init = ({
  id,
  selectedModel
}) => ({
  id,
  transcript: [],
  draft: "",
  selectedModel,
  defaultModel: selectedModel,
  modelPicker: Stateful.Select.init({
    id: `${id}-model-picker`,
    isAnimated: true
  }),
  runState: {
    _tag: "Idle"
  },
  nextRunNumber: 1,
  isFollowing: true,
  anchoredTurnId: "",
  currentTurnId: "",
  visibleTurnIds: [],
  copiedTurnId: "",
  announcement: "Agent ready."
});
var isActive = model => model.runState._tag === "Streaming" || model.runState._tag === "AwaitingPermission";
var transcriptId = model => `${model.id}-transcript`;
var turnElementId = (model, turnId) => `${model.id}-turn-${turnId}`;
var turnText = turn => turn.parts.filter(part => part._tag === "Text").map(part => part.text).join("\n\n");
var latestUserPrompt = model => {
  for (let index = model.transcript.length - 1; index >= 0; index -= 1) {
    const turn = model.transcript[index];
    if (turn?.role !== "User") continue;
    const part = turn.parts.find(item => item._tag === "Text");
    return part?._tag === "Text" ? part.text : "";
  }
  return "";
};
var mobile = "@media (max-width: 700px)";
var reducedMotion = "@media (prefers-reduced-motion: reduce)";
var className = (...values) => stylex.props(...values).className ?? "";
var styles = {
  root: {
    kWkggS: "xuhz2es",
    k1xSpc: "xrvj5dj",
    k9llMU: "xxq7klm",
    kZKoxP: "x5yr21d",
    kAzted: "x2lwn1j",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kVAEAm: "x1n2onr6",
    $$css: true
  },
  sessionBar: {
    kGNEyG: "x6s0dn4",
    kWkggS: "x11gw9ax",
    kL6WhQ: "x6fu8a9",
    kfdmCh: "x1q0q8m5",
    kt9PQ7: "xso031l",
    k1xSpc: "x78zum5",
    kOIVth: "x1v2ro7d",
    kjj79g: "x1qughib",
    k7Eaqz: "xeuugli",
    kmVPX3: "xdfu9ks x1wzyn1",
    $$css: true
  },
  sessionControls: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kOIVth: "x167g77z",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  modelMeta: {
    k1xSpc: "x78zum5 x1gz44f",
    kXwgrk: "xdt5ytf",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  modelProvider: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1k6wstc",
    k63SB2: "x19s9jnd",
    kb6lSQ: "x1vyo3qp",
    kP9fke: "xtvhhri",
    $$css: true
  },
  modelDescription: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1j6dyjg",
    keoZOQ: "xr9ek0c",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  transcript: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kAzted: "x2lwn1j",
    kXHlph: "x6ikm8r",
    kORKVm: "x1odjw0f",
    kmVPX3: "x1idi0x8 xv1l1y3",
    khEr5k: "xkcv6ua xg83hb1",
    k6w4Gs: "xyy94yx",
    $$css: true
  },
  transcriptInner: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1665zp3",
    ks0D6T: "x17q2glr",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  empty: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kUk6DE: "x98rzlu",
    kXwgrk: "xdt5ytf",
    kjj79g: "xl56j7k",
    kogj98: "x1bpp3o7",
    ks0D6T: "xic84rp",
    kmVPX3: "xyzs4rc",
    k9WMMc: "x2b8uid",
    $$css: true
  },
  emptyIcon: {
    kGNEyG: "x6s0dn4",
    kWkggS: "xwnonoy",
    kaIpWk: "xugzpu4",
    kMwMTN: "xzwifym",
    k1xSpc: "x78zum5",
    kZKoxP: "xnnlda6",
    kjj79g: "xl56j7k",
    k1K539: "x1c436fg",
    kzqmXN: "x15yg21f",
    $$css: true
  },
  emptyTitle: {
    kMwMTN: "x11jfisy",
    kGuDYH: "xneq65o xu9a7md",
    k63SB2: "x19s9jnd",
    kb6lSQ: "xrq0m5f",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  emptyCopy: {
    kMwMTN: "xrvchh",
    kGuDYH: "xif65rj",
    kLWn49: "x1dbl2gt",
    kogj98: "xno53fg",
    ks0D6T: "x27kpxv",
    $$css: true
  },
  suggestion: {
    khDVqt: "xuxw1ft xdw3p5r",
    $$css: true
  },
  turn: {
    k1xSpc: "x78zum5",
    kOIVth: "x15gj94b",
    k7Eaqz: "xeuugli",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  userTurn: {
    kXwgrk: "x15zctf7",
    $$css: true
  },
  avatar: {
    kGNEyG: "x6s0dn4",
    kWkggS: "xwnonoy",
    kaIpWk: "x1q4ynmn",
    kMwMTN: "x1lcixar",
    k1xSpc: "x78zum5",
    kmuXW: "x2lah0s",
    kZKoxP: "x1gnnpzl",
    kjj79g: "xl56j7k",
    kzqmXN: "x1849jeq",
    $$css: true
  },
  userAvatar: {
    kWkggS: "x8qxh4v",
    kMwMTN: "xgbnldg",
    $$css: true
  },
  turnBody: {
    k1xSpc: "x78zum5",
    kUk6DE: "x98rzlu",
    kXwgrk: "xdt5ytf",
    kOIVth: "xmmi0ta",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  userBody: {
    kGNEyG: "xuk3077",
    $$css: true
  },
  author: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1j6dyjg",
    k63SB2: "x19s9jnd",
    $$css: true
  },
  userBubble: {
    kWkggS: "x8qxh4v",
    kaIpWk: "x1o1v37g",
    kMwMTN: "xgbnldg",
    kGuDYH: "x4z9k3i",
    kLWn49: "x1jjo3f5",
    ks0D6T: "x3gi1w8",
    kmVPX3: "x1r1m0to",
    khDVqt: "x126k92a",
    $$css: true
  },
  assistantContent: {
    kMwMTN: "x11jfisy",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGuDYH: "x4z9k3i",
    kOIVth: "x1v2ro7d",
    kLWn49: "x11m8ghp",
    k7Eaqz: "xeuugli",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  text: {
    kogj98: "x1ghz6dp",
    khDVqt: "x126k92a",
    $$css: true
  },
  interrupted: {
    kMwMTN: "xrvchh",
    $$css: true
  },
  cursor: {
    kKVMdj: "x17gralb",
    k44tkh: "xegkacw x1u6grsq",
    ko0y90: "xa4qsjk",
    kWkggS: "x8qxh4v",
    kaIpWk: "x1nj7uno",
    k1xSpc: "x1rg5ohu",
    kZKoxP: "xlzyvqe",
    koQZXg: "x1swdo50",
    kSiTet: "x1hc1fzr",
    kXLuUW: "xijxtxk",
    kzqmXN: "xfo62xy",
    $$css: true
  },
  reasoning: {
    kWkggS: "x1s85zz8",
    kaIpWk: "xhpnuu7",
    kMwMTN: "xrvchh",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  reasoningSummary: {
    kGNEyG: "x6s0dn4",
    kkrTdU: "x1ypdohk",
    k1xSpc: "x78zum5",
    kOIVth: "x167g77z",
    kB88ic: "xe8uvvx",
    kAzted: "xu0aao5",
    kI3sdo: "x1a2a7pz x15triq5",
    kmVPX3: "x2lkgqm",
    $$css: true
  },
  reasoningIcon: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x3nfvp2",
    $$css: true
  },
  reasoningLabel: {
    kUk6DE: "x98rzlu",
    kGuDYH: "xfifm61",
    k63SB2: "x19s9jnd",
    $$css: true
  },
  reasoningText: {
    kLZC3w: "xio8jvi",
    kPef9Z: "x13fuv20",
    kEafiO: "x178xt8z",
    kGuDYH: "xfifm61",
    kLWn49: "x1dbl2gt",
    kogj98: "x1ghz6dp",
    kmVPX3: "x7z7khe",
    khDVqt: "x126k92a",
    $$css: true
  },
  messageActions: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kOIVth: "x195vfkc",
    keoZOQ: "x1rdy4ex",
    $$css: true
  },
  tool: {
    kWkggS: "x11gw9ax",
    kVAM5u: "x1io3bm2",
    kaIpWk: "x1q4ynmn",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xmkeg23",
    kVQacm: "xb3r6kr",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  toolSummary: {
    kGNEyG: "x6s0dn4",
    kkrTdU: "x1ypdohk",
    k1xSpc: "x78zum5",
    kOIVth: "x167g77z",
    kB88ic: "xe8uvvx",
    kAzted: "xgujvf1",
    kI3sdo: "x1a2a7pz x15triq5",
    kmVPX3: "x1q773pt",
    $$css: true
  },
  toolIcon: {
    kGNEyG: "x6s0dn4",
    kWkggS: "x1ut2sa4",
    kaIpWk: "xu4hgxq",
    kMwMTN: "xrvchh",
    k1xSpc: "x78zum5",
    kZKoxP: "xlrawln",
    kjj79g: "xl56j7k",
    kzqmXN: "xezl2tj",
    $$css: true
  },
  toolName: {
    kMwMTN: "x11jfisy",
    kUk6DE: "x98rzlu",
    kMv6JI: "x1mb2qyg",
    kGuDYH: "xfifm61",
    k63SB2: "x19s9jnd",
    $$css: true
  },
  toolDetails: {
    kLZC3w: "xio8jvi",
    kPef9Z: "x13fuv20",
    kEafiO: "x178xt8z",
    k1xSpc: "xrvj5dj",
    kOIVth: "x883omv",
    kmVPX3: "xaqq2fw",
    $$css: true
  },
  codeLabel: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1k6wstc",
    k63SB2: "x1xlr1w8",
    kb6lSQ: "x1dgsrnt",
    kP9fke: "xtvhhri",
    $$css: true
  },
  code: {
    kWkggS: "x1ut2sa4",
    kaIpWk: "xu4hgxq",
    kMwMTN: "x11jfisy",
    kMv6JI: "x1mb2qyg",
    kGuDYH: "x1j6dyjg",
    kLWn49: "x1jjo3f5",
    kogj98: "x1ghz6dp",
    kXHlph: "xw2csxc",
    kmVPX3: "x1uzaqoa",
    khDVqt: "x126k92a",
    kTgw9: "x13faqbe",
    $$css: true
  },
  permission: {
    kWkggS: "x1pc9hua",
    kVAM5u: "x13ncghc",
    kaIpWk: "xhpnuu7",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xmkeg23",
    k1xSpc: "xrvj5dj",
    kOIVth: "x883omv",
    kmVPX3: "xc7ga6q",
    $$css: true
  },
  permissionHeading: {
    kGNEyG: "x6s0dn4",
    kMwMTN: "x11jfisy",
    k1xSpc: "x78zum5",
    kGuDYH: "x4z9k3i",
    k63SB2: "x1xlr1w8",
    kOIVth: "x167g77z",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  permissionCopy: {
    kMwMTN: "xrvchh",
    kGuDYH: "xfifm61",
    kLWn49: "x1evy7pa",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  permissionActions: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kOIVth: "x167g77z",
    $$css: true
  },
  simulationNote: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1j6dyjg",
    $$css: true
  },
  composerShell: {
    kWkggS: "x3sdw5l",
    kmVPX3: "xbjizbe xvntl7c",
    $$css: true
  },
  composer: {
    kWkggS: "x11gw9ax",
    kVAM5u: "x1io3bm2",
    kaIpWk: "xixl9f9",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xmkeg23",
    kGVxlE: "x1apbbnd",
    k1xSpc: "xrvj5dj",
    kOIVth: "x167g77z",
    kogj98: "x19bbpc0",
    ks0D6T: "x17q2glr",
    kmVPX3: "xskserf",
    kzqmXN: "xh8yej3",
    $$css: true
  },
  textarea: {
    kMzoRj: "xc342km",
    kGVxlE: "x1gnnqk1",
    kAzted: "xt55aet",
    kHenm0: "xtt52l0",
    $$css: true
  },
  composerFooter: {
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kOIVth: "x883omv",
    kjj79g: "x1qughib",
    $$css: true
  },
  composerHelp: {
    kMwMTN: "xrvchh",
    kGuDYH: "x1k6wstc",
    kE3dHu: "xyms1uu",
    $$css: true
  },
  jump: {
    krVfgx: "xrsf3xo",
    kbCHJM: "x1nrll8i",
    kVAEAm: "x10l6tqk",
    k3aq6I: "xuuh30",
    kY2c9j: "xzkaem6",
    $$css: true
  },
  srOnly: {
    kMcinP: "xzpqnlu",
    kz4h6p: "x1hyvwdk",
    kZKoxP: "xjm9jq1",
    kVQacm: "xb3r6kr",
    kVAEAm: "x10l6tqk",
    khDVqt: "xuxw1ft",
    kzqmXN: "x1i1rx1s",
    $$css: true
  }
};

// src/components.ts
var ModelSelect = Stateful.Select.create();
var statusLabel = model => {
  if (model.runState._tag === "AwaitingPermission") return "Waiting for approval";
  if (model.runState._tag === "Failed") return "Failed";
  if (model.runState._tag === "Streaming") {
    const runningTool = model.transcript.some(turn => turn.parts.some(part => part._tag === "Tool" && part.status === "Running"));
    return runningTool ? "Using tool" : "Streaming";
  }
  const latestAssistant = [...model.transcript].reverse().find(turn => turn.role === "Assistant");
  const latestPart = latestAssistant?.parts.at(-1);
  if ((latestPart?._tag === "Text" || latestPart?._tag === "Reasoning") && latestPart.status === "Interrupted" || latestPart?._tag === "Tool" && latestPart.status === "Cancelled") return "Stopped";
  return model.transcript.length === 0 ? "Ready" : "Complete";
};
var toolTone = status => status === "Completed" ? "success" : status === "WaitingApproval" ? "warning" : status === "Denied" || status === "Cancelled" || status === "Failed" ? "danger" : "info";
var toolStatusLabel = status => status === "WaitingApproval" ? "Waiting for approval" : status;
var defaultPermissionResolution = part => part.status === "Denied" ? "Permission denied." : part.status === "Cancelled" ? "Request cancelled before execution." : "Permission allowed.";
var textResponse = (config, h) => config.part.status === "Complete" && config.renderComplete !== void 0 ? config.renderComplete(config.part, h) : h.p([h.Class(className(styles.text, config.part.status !== "Streaming" && styles.interrupted))], [config.part.text, ...(config.part.status === "Streaming" ? [h.span([h.Class(className(styles.cursor)), h.AriaHidden(true)])] : config.part.status === "Complete" ? [] : [h.span([], [` ${config.part.status === "Failed" ? "(failed)" : "(stopped)"}`])])]);
var reasoningPanel = (config, h) => h.details([h.Class(className(styles.reasoning)), h.Open(config.part.status === "Streaming"), h.DataAttribute("reasoning-status", config.part.status)], [h.summary([h.Class(className(styles.reasoningSummary))], [h.span([h.Class(className(styles.reasoningIcon)), h.AriaHidden(true)], [Icon.view({
  icon: Sparkles,
  size: 14
}, h)]), h.span([h.Class(className(styles.reasoningLabel))], [config.label ?? (config.part.status === "Streaming" ? "Reasoning\u2026" : "Reasoning")]), ...(config.part.status === "Streaming" ? [Spinner.view({
  label: "Reasoning in progress"
}, h)] : [])]), h.p([h.Class(className(styles.reasoningText))], [config.part.text, ...(config.part.status === "Interrupted" ? [h.span([h.Class(className(styles.interrupted))], [" (stopped)"])] : config.part.status === "Failed" ? [h.span([h.Class(className(styles.interrupted))], [" (failed)"])] : [])])]);
var messageActions = (config, h) => h.div([h.Class(className(styles.messageActions)), h.Role("group"), h.AriaLabel("Message actions")], [Button.view({
  label: config.isCopied === true ? "Copied" : "Copy",
  icon: config.isCopied === true ? Check : Copy,
  variant: "ghost",
  size: "sm",
  onClick: config.onCopy,
  isDisabled: !config.turn.parts.some(part => part._tag === "Text" && part.text)
}, h), ...(config.onRegenerate === void 0 ? [] : [Button.view({
  label: "Regenerate",
  icon: RefreshCcw,
  variant: "ghost",
  size: "sm",
  onClick: config.onRegenerate,
  isDisabled: config.canRegenerate === false
}, h)])]);
var permissionRequest = (config, h) => config.part.status === "WaitingApproval" ? h.section([h.Class(className(styles.permission)), h.Role("group"), h.AriaLabel(`Permission request for ${config.part.name}`)], [h.h3([h.Class(className(styles.permissionHeading))], [Icon.view({
  icon: LockKeyhole,
  size: 16
}, h), "Permission required"]), ...(config.part.permissionReason ? [h.p([h.Class(className(styles.permissionCopy))], [config.part.permissionReason])] : []), ...(config.presentation?.renderDetails?.(config.part, h) ?? []), h.div([h.Class(className(styles.permissionActions))], [Button.view({
  label: config.presentation?.allowLabel ?? "Allow once",
  icon: CheckCircle2,
  onClick: config.onDecision("Allow")
}, h), Button.view({
  label: config.presentation?.denyLabel ?? "Deny",
  icon: CircleX,
  variant: "outline",
  onClick: config.onDecision("Deny")
}, h), ...(config.presentation?.note === void 0 ? [] : [h.span([h.Class(className(styles.simulationNote))], [config.presentation.note])])])]) : h.div([h.Class(className(styles.permissionCopy))], [config.presentation?.resolutionText?.(config.part) ?? defaultPermissionResolution(config.part)]);
var toolCall = (config, h) => h.details([h.Class(className(styles.tool)), h.Open(config.part.status === "WaitingApproval"), h.DataAttribute("tool-call", config.part.name), h.DataAttribute("tool-status", config.part.status)], [h.summary([h.Class(className(styles.toolSummary))], [h.span([h.Class(className(styles.toolIcon)), h.AriaHidden(true)], [Icon.view({
  icon: Wrench,
  size: 14
}, h)]), h.span([h.Class(className(styles.toolName))], [config.part.name]), ...(config.part.status === "Running" ? [Spinner.view({
  label: "Tool running"
}, h)] : []), Badge.view({
  label: toolStatusLabel(config.part.status),
  tone: toolTone(config.part.status),
  dot: true
}, h)]), h.div([h.Class(className(styles.toolDetails))], [h.span([h.Class(className(styles.codeLabel))], ["Input"]), h.pre([h.Class(className(styles.code))], [config.part.input || "Waiting for input\u2026"]), ...(config.part.permissionReason || config.part.status === "WaitingApproval" ? [permissionRequest({
  part: config.part,
  onDecision: config.onPermissionDecision,
  ...(config.permission === void 0 ? {} : {
    presentation: config.permission
  })
}, h)] : []), ...(config.part.output ? [h.span([h.Class(className(styles.codeLabel))], ["Result"]), h.pre([h.Class(className(styles.code))], [config.part.output])] : [])])]);
var conversationPart = (part, config, h) => part._tag === "Text" ? textResponse({
  part,
  ...(config.renderText === void 0 ? {} : {
    renderComplete: config.renderText
  })
}, h) : part._tag === "Reasoning" ? reasoningPanel({
  part
}, h) : toolCall({
  part,
  onPermissionDecision: config.onPermissionDecision,
  ...(config.permission === void 0 ? {} : {
    permission: config.permission
  })
}, h);
var conversationTurn = (config, h) => {
  const own = config.turn.role === "User";
  const model = config.models?.find(option => option.id === config.turn.modelId);
  const assistantName = config.assistantName ?? model?.label ?? "Assistant";
  return h.article([h.Class(className(styles.turn, own && styles.userTurn)), ...(config.htmlId === void 0 ? [] : [h.Id(config.htmlId)]), h.AriaLabel(`${own ? "You" : assistantName} message`), h.DataAttribute("agent-turn", config.turn.role.toLowerCase()), h.DataAttribute("agent-turn-id", config.turn.id), h.DataAttribute("agent-scroll-anchor", config.scrollAnchor === true ? "true" : "false")], [h.span([h.Class(className(styles.avatar, own && styles.userAvatar)), h.AriaHidden(true)], [Icon.view({
    icon: own ? User : Bot,
    size: 15
  }, h)]), h.div([h.Class(className(styles.turnBody, own && styles.userBody))], [h.span([h.Class(className(styles.author))], [own ? "You" : assistantName]), ...(own ? [h.div([h.Class(className(styles.userBubble))], [config.turn.parts[0]?._tag === "Text" ? config.turn.parts[0].text : ""])] : [h.div([h.Class(className(styles.assistantContent))], config.turn.parts.length ? config.turn.parts.map(part => conversationPart(part, config, h)) : [h.div([], [Spinner.view({
    label: "Assistant is starting"
  }, h)])]), ...(config.actions === void 0 ? [] : [messageActions({
    turn: config.turn,
    ...config.actions
  }, h)])])])]);
};
var emptyState = (config, h) => h.div([h.Class(className(styles.empty))], [h.span([h.Class(className(styles.emptyIcon)), h.AriaHidden(true)], [Icon.view({
  icon: Sparkles,
  size: 25
}, h)]), h.h2([h.Class(className(styles.emptyTitle))], [config.title ?? "Start a conversation"]), h.p([h.Class(className(styles.emptyCopy))], [config.description ?? "Send a message to begin working with the agent."]), ...(config.suggestion === void 0 ? [] : [Button.view({
  label: config.suggestion.label,
  icon: Sparkles,
  size: "lg",
  sx: styles.suggestion,
  onClick: config.suggestion.onSelect
}, h)])]);
var sessionBar = (config, h) => {
  const selected = config.models.find(option => option.id === config.model.selectedModel);
  const active = isActive(config.model);
  const status = statusLabel(config.model);
  return h.header([h.Class(className(styles.sessionBar))], [h.div([h.Class(className(styles.sessionControls))], [h.submodel({
    slotId: config.model.modelPicker.id,
    model: config.model.modelPicker,
    view: ModelSelect.view,
    viewInputs: Stateful.Select.styledViewInputs({
      value: config.model.selectedModel,
      ariaLabel: config.modelAriaLabel ?? "Agent model",
      isDisabled: active,
      options: config.models.map(option => ({
        value: option.id,
        label: option.label
      }))
    }, h),
    toParentMessage: message => config.toParentMessage(Message.GotModelPickerMessage({
      message
    }))
  }), ...(selected === void 0 ? [] : [h.div([h.Class(className(styles.modelMeta))], [h.span([h.Class(className(styles.modelProvider))], [selected.provider]), h.span([h.Class(className(styles.modelDescription))], [selected.description])])])]), h.div([h.Class(className(styles.sessionControls))], [Badge.view({
    label: status,
    tone: status === "Failed" ? "danger" : status === "Waiting for approval" ? "warning" : status === "Complete" ? "success" : "info",
    dot: true
  }, h), Button.view({
    label: config.resetLabel ?? "Reset",
    icon: RotateCcw,
    variant: "ghost",
    size: "sm",
    onClick: config.toParentMessage(Message.Reset())
  }, h)])]);
};
var transcript = (config, h) => h.div([h.Class(className(styles.transcript)), h.Id(transcriptId(config.model)), h.Role("log"), h.AriaLive("off"), h.AriaLabel(config.ariaLabel ?? "Agent conversation"), h.AriaBusy(config.model.runState._tag === "Streaming"), h.DataAttribute("current-turn-id", config.model.currentTurnId), h.DataAttribute("visible-turn-count", String(config.model.visibleTurnIds.length)), h.Tabindex(0), h.OnScroll(scrollTop => config.toParentMessage(Message.ScrolledTranscript({
  scrollTop
})))], config.model.transcript.length === 0 ? [emptyState(config.empty ?? {}, h)] : [h.div([h.Class(className(styles.transcriptInner))], [...config.model.transcript.map(turn => conversationTurn({
  turn,
  htmlId: turnElementId(config.model, turn.id),
  scrollAnchor: turn.role === "User",
  onPermissionDecision: decision => config.toParentMessage(Message.ChosePermission({
    decision
  })),
  ...(config.models === void 0 ? {} : {
    models: config.models
  }),
  ...(config.assistantName === void 0 ? {} : {
    assistantName: config.assistantName
  }),
  ...(config.renderText === void 0 ? {} : {
    renderText: config.renderText
  }),
  ...(config.permission === void 0 ? {} : {
    permission: config.permission
  }),
  ...(turn.role !== "Assistant" || config.showMessageActions === false ? {} : {
    actions: {
      isCopied: config.model.copiedTurnId === turn.id,
      canRegenerate: !isActive(config.model) && turn.id === [...config.model.transcript].reverse().find(candidate => candidate.role === "Assistant")?.id,
      onCopy: config.toParentMessage(Message.CopiedTurn({
        turnId: turn.id
      })),
      onRegenerate: config.toParentMessage(Message.RegeneratedTurn({
        turnId: turn.id
      }))
    }
  })
}, h)), ...(config.model.runState._tag === "Failed" ? [Alert.view({
  title: config.failureTitle ?? "The agent run failed",
  description: config.model.runState.message,
  tone: "danger",
  children: [Button.view({
    label: config.retryLabel ?? "Retry",
    icon: RotateCcw,
    variant: "outline",
    onClick: config.toParentMessage(Message.Retried())
  }, h)]
}, h)] : [])])]);
var composer = (config, h) => {
  const active = isActive(config.model);
  const inputId = `${config.model.id}-prompt`;
  const helpId = `${config.model.id}-composer-help`;
  return h.div([h.Class(className(styles.composerShell))], [h.form([h.Class(className(styles.composer)), h.OnSubmit(config.toParentMessage(Message.Submitted())), h.AriaLabel(config.ariaLabel ?? "Agent prompt")], [h.label([h.Class(className(styles.srOnly)), h.For(inputId)], ["Message the agent"]), Textarea.view({
    id: inputId,
    value: config.model.draft,
    rows: 2,
    placeholder: active ? config.activePlaceholder ?? "Wait for the current run to finish\u2026" : config.placeholder ?? "Message the agent\u2026",
    ariaLabel: "Message the agent",
    isDisabled: active,
    onInput: value => config.toParentMessage(Message.ChangedDraft({
      value
    })),
    sx: styles.textarea,
    attributes: [h.AriaDescribedBy(helpId), h.OnKeyDownPreventDefault((key, modifiers) => key === "Enter" && !modifiers.shiftKey ? Option.some(config.toParentMessage(Message.Submitted())) : Option.none())]
  }, h), h.div([h.Class(className(styles.composerFooter))], [h.span([h.Class(className(styles.composerHelp)), h.Id(helpId)], [config.helpText ?? "Enter to send \xB7 Shift+Enter for a new line"]), active ? Button.view({
    label: config.stopLabel ?? "Stop",
    icon: CircleStop,
    variant: "danger",
    onClick: config.toParentMessage(Message.Stopped())
  }, h) : Button.view({
    label: config.sendLabel ?? "Send",
    icon: Send,
    onClick: config.toParentMessage(Message.Submitted()),
    isDisabled: !config.model.draft.trim()
  }, h)])])]);
};
var chat = (config, h) => {
  const empty = {
    ...(config.empty?.title === void 0 ? {} : {
      title: config.empty.title
    }),
    ...(config.empty?.description === void 0 ? {} : {
      description: config.empty.description
    }),
    ...(config.empty?.suggestion === void 0 ? {} : {
      suggestion: {
        label: config.empty.suggestion.label,
        onSelect: config.toParentMessage(Message.SelectedSuggestion({
          prompt: config.empty.suggestion.prompt
        }))
      }
    })
  };
  return h.section([h.Class(className(styles.root)), h.DataAttribute("agent-chat", "true")], [sessionBar({
    model: config.model,
    models: config.models,
    toParentMessage: config.toParentMessage
  }, h), transcript({
    model: config.model,
    models: config.models,
    toParentMessage: config.toParentMessage,
    empty,
    ...(config.assistantName === void 0 ? {} : {
      assistantName: config.assistantName
    }),
    ...(config.transcriptAriaLabel === void 0 ? {} : {
      ariaLabel: config.transcriptAriaLabel
    }),
    ...(config.renderText === void 0 ? {} : {
      renderText: config.renderText
    }),
    ...(config.permission === void 0 ? {} : {
      permission: config.permission
    }),
    ...(config.failureTitle === void 0 ? {} : {
      failureTitle: config.failureTitle
    }),
    ...(config.retryLabel === void 0 ? {} : {
      retryLabel: config.retryLabel
    }),
    ...(config.showMessageActions === void 0 ? {} : {
      showMessageActions: config.showMessageActions
    })
  }, h), ...(!config.model.isFollowing && config.model.transcript.length ? [h.div([h.Class(className(styles.jump))], [Button.view({
    label: config.jumpLabel ?? "Jump to latest",
    variant: "secondary",
    size: "sm",
    onClick: config.toParentMessage(Message.JumpedLatest())
  }, h)])] : []), composer({
    model: config.model,
    toParentMessage: config.toParentMessage,
    ...(config.composer ?? {})
  }, h), h.div([h.Class(className(styles.srOnly)), h.Role("status"), h.AriaLive("polite"), h.AriaAtomic(true)], [config.model.announcement])]);
};
var Chat = {
  view: chat
};
var SessionBar = {
  view: sessionBar
};
var Transcript = {
  view: transcript
};
var ConversationTurn = {
  view: conversationTurn
};
var TextResponse = {
  view: textResponse
};
var ReasoningPanel = {
  view: reasoningPanel
};
var MessageActions = {
  view: messageActions
};
var ToolCall = {
  view: toolCall
};
var PermissionRequest = {
  view: permissionRequest
};
var EmptyState = {
  view: emptyState
};
var Composer = {
  view: composer
};
var MeasureTranscript = Command.define("MeasureAgentTranscript", {
  args: {
    id: Schema.String
  },
  messages: [Message.CompletedMeasureTranscript],
  execute: ({
    id
  }) => Effect.promise(() => new Promise(resolve => {
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (element === null) {
        resolve(Message.CompletedMeasureTranscript({
          isFollowing: true,
          currentTurnId: "",
          visibleTurnIds: []
        }));
        return;
      }
      const viewport = element.getBoundingClientRect();
      const turns = Array.from(element.querySelectorAll("[data-agent-turn-id]"));
      const visibleTurnIds = turns.filter(turn => {
        const bounds = turn.getBoundingClientRect();
        return bounds.bottom > viewport.top && bounds.top < viewport.bottom;
      }).map(turn => turn.dataset.agentTurnId ?? "").filter(Boolean);
      const anchors = turns.filter(turn => turn.dataset.agentScrollAnchor === "true");
      const currentAnchor = [...anchors].reverse().find(turn => turn.getBoundingClientRect().top <= viewport.top + 72);
      resolve(Message.CompletedMeasureTranscript({
        isFollowing: element.scrollHeight - element.clientHeight - element.scrollTop < 72,
        currentTurnId: currentAnchor?.dataset.agentTurnId ?? visibleTurnIds[0] ?? "",
        visibleTurnIds
      }));
    });
  }))
});
var ScrollLatest = Command.define("ScrollAgentTranscriptLatest", {
  args: {
    id: Schema.String
  },
  messages: [Message.CompletedScrollLatest],
  execute: ({
    id
  }) => Effect.promise(() => new Promise(resolve => {
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (element !== null) element.scrollTop = element.scrollHeight;
      resolve(Message.CompletedScrollLatest());
    });
  }))
});
var ScrollToTurn = Command.define("ScrollAgentTranscriptToTurn", {
  args: {
    id: Schema.String,
    turnId: Schema.String,
    previousItemPeek: Schema.Number
  },
  messages: [Message.CompletedScrollToTurn],
  execute: ({
    id,
    turnId,
    previousItemPeek
  }) => Effect.promise(() => new Promise(resolve => {
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      const turn = element === null ? void 0 : Array.from(element.querySelectorAll("[data-agent-turn-id]")).find(candidate => candidate.dataset.agentTurnId === turnId);
      if (element !== null && turn !== void 0) {
        const viewport = element.getBoundingClientRect();
        const bounds = turn.getBoundingClientRect();
        element.scrollTop += bounds.top - viewport.top - Math.max(0, previousItemPeek);
      }
      resolve(Message.CompletedScrollToTurn({
        turnId
      }));
    });
  }))
});
var CopyTurn = Command.define("CopyAgentTurn", {
  args: {
    turnId: Schema.String,
    text: Schema.String
  },
  messages: [Message.CompletedCopyTurn],
  execute: ({
    turnId,
    text
  }) => Effect.promise(async () => {
    await navigator.clipboard.writeText(text);
    return Message.CompletedCopyTurn({
      turnId
    });
  })
});
var ClearCopiedTurn = Command.define("ClearCopiedAgentTurn", {
  args: {
    turnId: Schema.String
  },
  messages: [Message.ClearedCopiedTurn],
  execute: ({
    turnId
  }) => Effect.sleep("2 seconds").pipe(Effect.as(Message.ClearedCopiedTurn({
    turnId
  })))
});
var mapParts = (model, predicate, transform) => ({
  ...model,
  transcript: model.transcript.map(turn => turn.role !== "Assistant" ? turn : {
    ...turn,
    parts: turn.parts.map(part => predicate(part) ? transform(part) : part)
  })
});
var appendAssistantPart = (model, part) => {
  let index = -1;
  for (let turnIndex = model.transcript.length - 1; turnIndex >= 0; turnIndex -= 1) {
    if (model.transcript[turnIndex]?.role === "Assistant") {
      index = turnIndex;
      break;
    }
  }
  if (index < 0) return model;
  return {
    ...model,
    transcript: model.transcript.map((turn, turnIndex) => turnIndex === index ? {
      ...turn,
      parts: [...turn.parts, part]
    } : turn)
  };
};
var updateText = (model, partId, transform) => mapParts(model, part => part._tag === "Text" && part.id === partId, part => transform(part));
var updateReasoning = (model, partId, transform) => mapParts(model, part => part._tag === "Reasoning" && part.id === partId, part => transform(part));
var updateTool = (model, callId, transform) => mapParts(model, part => part._tag === "Tool" && part.callId === callId, part => transform(part));
var startRun = (model, prompt, appendUser) => {
  const value = prompt.trim();
  if (!value || isActive(model)) return {
    model
  };
  const number = model.nextRunNumber;
  const runId = `${model.id}-run-${number}`;
  const userTurnId = `${runId}-user`;
  const turns = [...(appendUser ? [{
    id: userTurnId,
    role: "User",
    modelId: "",
    parts: [{
      _tag: "Text",
      id: `${runId}-prompt`,
      text: value,
      status: "Complete"
    }]
  }] : []), {
    id: `${runId}-assistant`,
    role: "Assistant",
    modelId: model.selectedModel,
    parts: []
  }];
  return {
    model: {
      ...model,
      transcript: [...model.transcript, ...turns],
      draft: "",
      runState: {
        _tag: "Streaming",
        runId,
        segment: "Initial",
        lastSequence: 0
      },
      nextRunNumber: number + 1,
      isFollowing: true,
      anchoredTurnId: appendUser ? userTurnId : model.anchoredTurnId,
      announcement: "Assistant response started."
    },
    commands: appendUser ? [ScrollToTurn({
      id: transcriptId(model),
      turnId: userTurnId,
      previousItemPeek: 64
    })] : [ScrollLatest({
      id: transcriptId(model)
    })]
  };
};
var applyEvent = (model, event, sequence) => {
  const streaming = model.runState._tag === "Streaming" ? {
    ...model.runState,
    lastSequence: sequence
  } : model.runState;
  let next = {
    ...model,
    runState: streaming
  };
  switch (event._tag) {
    case "Started":
      return {
        ...next,
        announcement: "Assistant is streaming a response."
      };
    case "TextStarted":
      return appendAssistantPart(next, {
        _tag: "Text",
        id: event.partId,
        text: "",
        status: "Streaming"
      });
    case "TextDelta":
      return updateText(next, event.partId, part => ({
        ...part,
        text: part.text + event.delta
      }));
    case "TextFinished":
      return updateText(next, event.partId, part => ({
        ...part,
        status: "Complete"
      }));
    case "ReasoningStarted":
      return appendAssistantPart(next, {
        _tag: "Reasoning",
        id: event.partId,
        text: "",
        status: "Streaming"
      });
    case "ReasoningDelta":
      return updateReasoning(next, event.partId, part => ({
        ...part,
        text: part.text + event.delta
      }));
    case "ReasoningFinished":
      return updateReasoning(next, event.partId, part => ({
        ...part,
        status: "Complete"
      }));
    case "ToolInputStarted":
      return appendAssistantPart({
        ...next,
        announcement: `${event.name} tool call prepared.`
      }, {
        _tag: "Tool",
        callId: event.callId,
        name: event.name,
        input: "",
        output: "",
        permissionReason: "",
        status: "Preparing"
      });
    case "ToolInputDelta":
      return updateTool(next, event.callId, part => ({
        ...part,
        input: part.input + event.delta
      }));
    case "ToolCallReady":
      return {
        ...next,
        announcement: "Tool input is ready."
      };
    case "ToolStarted":
      return updateTool({
        ...next,
        announcement: "Tool is running."
      }, event.callId, part => ({
        ...part,
        status: "Running"
      }));
    case "ToolResult":
      return updateTool({
        ...next,
        announcement: "Tool completed."
      }, event.callId, part => ({
        ...part,
        status: "Completed",
        output: event.output
      }));
    case "PermissionRequested":
      next = updateTool(next, event.callId, part => ({
        ...part,
        permissionReason: event.reason,
        status: "WaitingApproval"
      }));
      return {
        ...next,
        runState: {
          _tag: "AwaitingPermission",
          runId: event.runId,
          callId: event.callId,
          lastSequence: sequence
        },
        announcement: "Permission required. Review the tool request."
      };
    case "Finished":
      return {
        ...next,
        runState: {
          _tag: "Idle"
        },
        announcement: "Assistant response complete."
      };
    case "Failed":
      next = mapParts(next, part => (part._tag === "Text" || part._tag === "Reasoning") && part.status === "Streaming", part => part._tag === "Text" || part._tag === "Reasoning" ? {
        ...part,
        status: "Failed"
      } : part);
      next = mapParts(next, part => part._tag === "Tool" && ["Preparing", "Running"].includes(part.status), part => part._tag === "Tool" ? {
        ...part,
        status: "Failed"
      } : part);
      return {
        ...next,
        runState: {
          _tag: "Failed",
          runId: event.runId,
          message: event.message
        },
        announcement: `Agent failed: ${event.message}`
      };
  }
};
var receive = (model, envelope) => {
  if (model.runState._tag !== "Streaming" || envelope.event.runId !== model.runState.runId || envelope.sequence <= model.runState.lastSequence) return {
    model
  };
  const next = applyEvent(model, envelope.event, envelope.sequence);
  return {
    model: next,
    commands: model.anchoredTurnId ? [MeasureTranscript({
      id: transcriptId(model)
    })] : model.isFollowing ? [ScrollLatest({
      id: transcriptId(model)
    })] : [MeasureTranscript({
      id: transcriptId(model)
    })]
  };
};
var interrupt = model => {
  let next = mapParts(model, part => (part._tag === "Text" || part._tag === "Reasoning") && part.status === "Streaming", part => part._tag === "Text" || part._tag === "Reasoning" ? {
    ...part,
    status: "Interrupted"
  } : part);
  next = mapParts(next, part => part._tag === "Tool" && ["Preparing", "Running", "WaitingApproval"].includes(part.status), part => part._tag === "Tool" ? {
    ...part,
    status: "Cancelled",
    output: part.output || "Cancelled before execution."
  } : part);
  return {
    ...next,
    runState: {
      _tag: "Idle"
    },
    announcement: "Assistant response stopped."
  };
};
var foldModelPicker = Update.foldChild({
  update: ModelSelect.update,
  read: model => Option.some(model.modelPicker),
  write: (model, modelPicker) => ({
    ...model,
    modelPicker
  }),
  toParentMessage: message => Message.GotModelPickerMessage({
    message
  }),
  foldOutMessage: outMessage => model => isActive(model) ? {
    model
  } : {
    model: {
      ...model,
      selectedModel: outMessage.value,
      announcement: `${outMessage.value} selected.`
    }
  }
});
var update = (model, message) => Message.match(message, {
  GotModelPickerMessage: ({
    message: message2
  }) => foldModelPicker(model, message2),
  ChangedDraft: ({
    value
  }) => isActive(model) ? {
    model
  } : {
    model: {
      ...model,
      draft: value
    }
  },
  Submitted: () => startRun(model, model.draft, true),
  SelectedSuggestion: ({
    prompt
  }) => startRun({
    ...model,
    draft: prompt
  }, prompt, true),
  ReceivedStreamEvent: ({
    envelope
  }) => receive(model, envelope),
  ChosePermission: ({
    decision
  }) => {
    if (model.runState._tag !== "AwaitingPermission") return {
      model
    };
    const {
      runId,
      callId,
      lastSequence
    } = model.runState;
    const allowed = decision === "Allow";
    const next = updateTool(model, callId, part => ({
      ...part,
      status: allowed ? "Running" : "Denied",
      output: allowed ? part.output : "Permission denied. No action was taken."
    }));
    return {
      model: {
        ...next,
        runState: {
          _tag: "Streaming",
          runId,
          segment: allowed ? "Approved" : "Denied",
          lastSequence
        },
        announcement: allowed ? "Permission granted once. Tool is running." : "Permission denied. No action was taken."
      }
    };
  },
  Stopped: () => isActive(model) ? {
    model: interrupt(model)
  } : {
    model
  },
  Retried: () => model.runState._tag === "Failed" ? startRun({
    ...model,
    runState: {
      _tag: "Idle"
    }
  }, latestUserPrompt(model), false) : {
    model
  },
  RegeneratedTurn: ({
    turnId
  }) => {
    if (isActive(model)) return {
      model
    };
    const index = model.transcript.findIndex(turn => turn.id === turnId && turn.role === "Assistant");
    const latestAssistantIndex = model.transcript.findLastIndex(turn => turn.role === "Assistant");
    if (index < 0 || index !== latestAssistantIndex) return {
      model
    };
    const prompt = [...model.transcript.slice(0, index)].reverse().find(turn => turn.role === "User")?.parts.find(part => part._tag === "Text")?.text ?? "";
    return startRun({
      ...model,
      transcript: model.transcript.filter((_, turnIndex) => turnIndex !== index)
    }, prompt, false);
  },
  CopiedTurn: ({
    turnId
  }) => {
    const turn = model.transcript.find(candidate => candidate.id === turnId);
    const text = turn === void 0 ? "" : turnText(turn);
    return text ? {
      model,
      commands: [CopyTurn({
        turnId,
        text
      })]
    } : {
      model
    };
  },
  CompletedCopyTurn: ({
    turnId
  }) => ({
    model: {
      ...model,
      copiedTurnId: turnId,
      announcement: "Response copied."
    },
    commands: [ClearCopiedTurn({
      turnId
    })]
  }),
  ClearedCopiedTurn: ({
    turnId
  }) => ({
    model: model.copiedTurnId === turnId ? {
      ...model,
      copiedTurnId: ""
    } : model
  }),
  Reset: () => ({
    model: {
      ...init({
        id: model.id,
        selectedModel: model.defaultModel
      }),
      nextRunNumber: model.nextRunNumber
    }
  }),
  ScrolledTranscript: () => ({
    model,
    commands: [MeasureTranscript({
      id: transcriptId(model)
    })]
  }),
  CompletedMeasureTranscript: ({
    isFollowing,
    currentTurnId,
    visibleTurnIds
  }) => ({
    model: {
      ...model,
      isFollowing,
      currentTurnId,
      visibleTurnIds
    }
  }),
  JumpedLatest: () => ({
    model: {
      ...model,
      isFollowing: true,
      anchoredTurnId: ""
    },
    commands: [ScrollLatest({
      id: transcriptId(model)
    })]
  }),
  JumpedToTurn: ({
    turnId
  }) => model.transcript.some(turn => turn.id === turnId) ? {
    model: {
      ...model,
      isFollowing: false,
      anchoredTurnId: turnId
    },
    commands: [ScrollToTurn({
      id: transcriptId(model),
      turnId,
      previousItemPeek: 64
    })]
  } : {
    model
  },
  CompletedScrollLatest: () => ({
    model
  }),
  CompletedScrollToTurn: ({
    turnId
  }) => ({
    model: {
      ...model,
      currentTurnId: turnId
    },
    commands: [MeasureTranscript({
      id: transcriptId(model)
    })]
  })
});

// src/agent.ts
var Message2 = Message;
var Model2 = Model;
var ConversationPart2 = ConversationPart;
var ReasoningPart2 = ReasoningPart;
var RunState2 = RunState;
var TextPart2 = TextPart;
var ToolPart2 = ToolPart;
var Turn2 = Turn;
var EventEnvelope2 = EventEnvelope;
var Segment2 = Segment;
var StreamEvent2 = StreamEvent;
export { agent_exports as Agent };